import React, { useMemo } from 'react';
import { Grid, Switch } from '@arco-design/web-react';
import { useBlock, useFocusIdx } from '@bay-email/editor';
import { t } from '@bay-email/core';
import { get } from 'lodash';

import { BorderWidth } from '../../attributes/BorderWidth';
import { InputWithUnitField } from '../../../../components/Form';
import { pixelAdapter } from '../../adapter';

const BORDER_SIDE_KEYS = [
	'border-top-width',
	'border-right-width',
	'border-bottom-width',
	'border-left-width',
] as const;

const normalize = (value: unknown) => String(value ?? '').trim();

export function BorderWidthControl() {
	const { focusIdx } = useFocusIdx();
	const { values, change } = useBlock();

	const borderWidthPath = `${focusIdx}.attributes.border-width`;
	const topPath = `${focusIdx}.attributes.border-top-width`;
	const rightPath = `${focusIdx}.attributes.border-right-width`;
	const bottomPath = `${focusIdx}.attributes.border-bottom-width`;
	const leftPath = `${focusIdx}.attributes.border-left-width`;

	const borderWidth = get(values, borderWidthPath);
	const sideValues = useMemo(
		() => [
			get(values, topPath),
			get(values, rightPath),
			get(values, bottomPath),
			get(values, leftPath),
		],
		[values, topPath, rightPath, bottomPath, leftPath],
	);
	const isLinked = useMemo(() => !sideValues.map(normalize).some(Boolean), [sideValues]);

	const handleLinkChange = (checked: boolean) => {
		const normalizedBorderWidth = normalize(borderWidth);
		const firstSideValue = sideValues.map(normalize).find(Boolean);
		const fallbackWidth = normalizedBorderWidth || firstSideValue || '0px';

		if (checked) {
			change(borderWidthPath, fallbackWidth);
			BORDER_SIDE_KEYS.forEach(sideKey => {
				change(`${focusIdx}.attributes.${sideKey}`, '');
			});
			return;
		}

		BORDER_SIDE_KEYS.forEach(sideKey => {
			change(`${focusIdx}.attributes.${sideKey}`, fallbackWidth);
		});
	};

	return (
		<>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					marginBottom: '12px',
					gap: '12px',
				}}
			>
				<span>{t('Link Sides')}</span>
				<Switch
					checked={isLinked}
					onChange={handleLinkChange}
					size='small'
				/>
			</div>

			{isLinked ? (
				<Grid.Row gutter={16}>
					<Grid.Col span={12}>
						<BorderWidth
							secondary
							config={pixelAdapter}
							label={t('Thickness (px)')}
						/>
					</Grid.Col>
				</Grid.Row>
			) : (
				<>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<InputWithUnitField
								label={t('Top (px)')}
								secondaryLabel
								name={topPath}
								config={pixelAdapter}
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							<InputWithUnitField
								label={t('Right (px)')}
								secondaryLabel
								name={rightPath}
								config={pixelAdapter}
							/>
						</Grid.Col>
					</Grid.Row>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<InputWithUnitField
								label={t('Bottom (px)')}
								secondaryLabel
								name={bottomPath}
								config={pixelAdapter}
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							<InputWithUnitField
								label={t('Left (px)')}
								secondaryLabel
								name={leftPath}
								config={pixelAdapter}
							/>
						</Grid.Col>
					</Grid.Row>
				</>
			)}
		</>
	);
}
